/**
 * Amplifier profiles -- group 01 (North American and Canadian high end).
 *
 * These eight are mostly transistor designs, which makes the differentiation
 * work harder: the separating variables are output-stage bias scheme (fixed
 * Class A, sliding Class A, conventional Class AB, switching), how much global
 * feedback is applied and therefore what the residue looks like at low level,
 * source impedance seen by the loudspeaker, and supply stiffness under
 * sustained load. Two of the entries are not power amplifiers at all -- a
 * balanced headphone amplifier and a line-stage preamplifier -- because the
 * chain model needs those slots filled with something real.
 *
 * None of these manufacturers builds an electrostatic headphone energiser, so
 * no entry here claims that form.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_01: AmpProfile[] = [
  makeAmp({
    mfr: 'McIntosh Laboratory',
    model: 'MC275 Mk VI',
    price: usd(6500, 'APPROXIMATE', 2023, 'US list for the current production Mk VI; the price has risen steadily across the run.'),
    year: 2012,
    country: 'United States',
    topology: 'tube-push-pull',
    form: 'stereo-power',
    devices: '4 x KT88 push-pull per channel pair, 12AT7 and 12AX7 front end, Unity Coupled bifilar output transformer',
    power: { watts8: 75, watts4: 75, watts2: 75, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 27,
    thdPercent: 0.5,
    snrDb: 95,
    bandwidth: [12, 65000],
    outputTransformer: true,
    transformerLfHz: 16,
    transformerHfHz: 55000,
    dampingFactor: 18,
    feedbackDb: 12,
    crosstalkDb: -70,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    dynamics: { sagDb: 0.9, sagTimeMs: 160, recoveryMs: 380, slewRateVus: 20, headroomDb: 1.3 },
    clipping: { kind: 'soft', kneeDb: 6.5, stickiness: 0.3 },
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f8fd0',
    tags: ['kt88', 'unity-coupled', 'autoformer', 'push-pull'],
    notes:
      'The Unity Coupled output transformer with its autoformer secondary is the reason this amplifier does not behave like other tube designs: rated power is the same into 2, 4 and 8 ohms because the load is matched by tap rather than by letting output voltage collapse, and the reflected source impedance stays near 0.44 ohm on every tap. Push-pull operation cancels most of the second harmonic, so the residual spectrum is third-harmonic-dominant, unlike the single-ended designs elsewhere in this database. What remains tube-like is the soft clipping knee and the supply sag on sustained peaks.',
    provenanceNote:
      'Rated power on all taps, damping factor, signal-to-noise ratio and the 0.5 percent THD limit are McIntosh published specifications. The modelled transformer corner frequencies are chosen to be consistent with the published response window rather than measured; McIntosh publishes a narrow-band response tolerance and a wider -3 dB claim, and the values here sit between them.',
  }),

  makeAmp({
    mfr: 'Mark Levinson',
    model: 'No. 5302',
    price: usd(7000, 'APPROXIMATE', 2020, 'US list at introduction.'),
    year: 2019,
    country: 'United States',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Fully balanced dual-monaural, discrete bipolar output, six complementary pairs per channel',
    power: { watts8: 135, watts4: 270, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 26,
    thdPercent: 0.02,
    snrDb: 105,
    bandwidth: [2, 100000],
    dampingFactor: 400,
    feedbackDb: 34,
    crosstalkDb: -100,
    balancedInput: true,
    dynamics: { sagDb: 0.25, sagTimeMs: 50, recoveryMs: 130, thermalDb: 0.1, slewRateVus: 70, headroomDb: 2.2 },
    clipping: { kind: 'asymmetric', kneeDb: 2.0, stickiness: 0.07 },
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c9ced6',
    tags: ['dual-mono', 'balanced', 'high-feedback', 'doubles-into-4'],
    notes:
      'Exactly doubling power into halved impedance is the published claim, and it is the useful engineering fact: the supply does not sag appreciably, so the dynamic model gives this amplifier a much smaller sag term than the tube and switching entries. High global feedback drives total distortion below 0.02 percent, but feedback does not remove the crossover discontinuity, it only shrinks it and pushes what is left into higher orders. The model reflects that by keeping the class-B crossover nonlinearity narrow but not eliminating it, so the residue becomes proportionally more significant at low listening levels.',
    provenanceNote:
      'Power into 8 and 4 ohms, THD and the balanced dual-monaural topology are Mark Levinson published specifications. Damping factor, feedback depth and the crossover width used by the nonlinear model are ESTIMATED from the topology; Mark Levinson does not publish a crossover-region characterisation.',
  }),

  makeAmp({
    mfr: 'Krell Industries',
    model: 'Solo 575 XD',
    price: usd(19000, 'APPROXIMATE', 2023, 'Price per monoblock; XD-series pricing varies by market.'),
    year: 2019,
    country: 'United States',
    topology: 'solid-state-a',
    form: 'monoblock',
    devices: 'Krell iBias sliding-bias Class A output, large complementary bipolar array, direct coupled',
    power: { watts8: 575, watts4: 1150, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.05,
    snrDb: 110,
    bandwidth: [1, 100000],
    outputImpedanceOhms: 0.012,
    feedbackDb: 24,
    crosstalkDb: -95,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.35, asymmetry: 0.3, bias: 0.45, headroomDb: 14, oversample: 4 },
    dynamics: { sagDb: 0.12, sagTimeMs: 40, recoveryMs: 110, thermalDb: 0.3, thermalTimeMs: 9000, slewRateVus: 90, headroomDb: 2.6 },
    clipping: { kind: 'asymmetric', kneeDb: 3.0, stickiness: 0.1 },
    confidence: 'ESTIMATED',
    accent: '#d95f43',
    tags: ['ibias', 'class-a', 'high-current', 'monoblock'],
    notes:
      'iBias measures instantaneous output current and moves the output-stage bias to keep the devices conducting through the whole cycle, which is a different mechanism from the fixed heavy bias of a conventional Class A amplifier and from the fixed light bias of a Class AB one. The modelled consequence is that the crossover-region discontinuity that dominates a Class AB residue at low level is essentially absent here, so distortion is low-order and rises monotonically with drive rather than being worst at small signals. Combined with a milliohm-class output impedance the amplifier behaves as a near-ideal voltage source into a difficult reactive load.',
    provenanceNote:
      'The iBias bias-tracking scheme and the monoblock configuration are from Krell published material. Rated power into 8 and 4 ohms, THD, signal-to-noise ratio and output impedance are ESTIMATED at levels consistent with the published class of the amplifier rather than quoted from a datasheet.',
  }),

  makeAmp({
    mfr: 'Classe Audio',
    model: 'Sigma AMP2',
    price: usd(3500, 'APPROXIMATE', 2016, 'US list at introduction; the Sigma line is discontinued.'),
    year: 2015,
    country: 'Canada',
    topology: 'class-d',
    form: 'stereo-power',
    devices: 'Switching output stage with linear input buffer and post-filter feedback, switch-mode supply',
    power: { watts8: 200, watts4: 400, confidence: 'ESTIMATED' },
    gainDb: 29,
    thdPercent: 0.006,
    snrDb: 112,
    bandwidth: [10, 40000],
    dampingFactor: 250,
    feedbackDb: 24,
    crosstalkDb: -92,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.42, carrierHz: 400000, headroomDb: 8, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.1, sagTimeMs: 20, recoveryMs: 55, slewRateVus: 22, headroomDb: 0.7 },
    clipping: { kind: 'hard', kneeDb: 0.4, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#7f8c9a',
    tags: ['switching', 'compact', 'lc-output-filter'],
    notes:
      'The distinguishing behaviour of a switching output stage is that the reconstruction filter sits between the transistors and the loudspeaker, so its corner frequency is set partly by the load: response above roughly 10 kHz shifts with speaker impedance in a way no linear amplifier does, and it is the reason this profile is given a much lower high-frequency corner than the transistor amplifiers around it. Loop gain also falls with frequency, so the residual distortion is odd-order and rises toward the top of the band rather than staying flat. Clipping is abrupt because there is no reservoir of linear headroom above the rail.',
    provenanceNote:
      'Classe describes the Sigma series as a switching design; power ratings, THD, signal-to-noise ratio and bandwidth here are ESTIMATED at levels typical of that generation of module-based amplifier rather than quoted, and the switching carrier frequency is a modelling assumption. Verify against the original Classe datasheet before treating any figure as published.',
  }),

  makeAmp({
    mfr: 'Bryston',
    model: 'BHA-1',
    price: usd(1495, 'APPROXIMATE', 2018, 'US list; Bryston pricing has moved with the Canadian dollar over the product life.'),
    year: 2013,
    country: 'Canada',
    topology: 'solid-state-a',
    form: 'headphone',
    devices: 'Four independent discrete Class A amplifier channels, bridgeable for balanced drive, switchable gain',
    power: { mw32: 900, mw300: 400, mw600: 220, confidence: 'ESTIMATED' },
    gainDb: 18,
    thdPercent: 0.006,
    snrDb: 110,
    bandwidth: [2, 150000],
    outputImpedanceOhms: 1.0,
    feedbackDb: 20,
    crosstalkDb: -88,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.3, asymmetry: 0.35, bias: 0.5, headroomDb: 14, oversample: 4 },
    dynamics: { sagDb: 0.05, sagTimeMs: 30, recoveryMs: 80, thermalDb: 0.15, slewRateVus: 55, headroomDb: 3.0 },
    clipping: { kind: 'asymmetric', kneeDb: 2.5, stickiness: 0.08 },
    confidence: 'ESTIMATED',
    accent: '#4b9e6a',
    tags: ['headphone', 'balanced-drive', 'discrete', 'class-a'],
    notes:
      'Four separate single-ended channels that can be operated as two bridged pairs. In bridged mode each earpiece is driven from two outputs in antiphase, which doubles the available voltage swing and cancels the even-order component between the halves, so the harmonic structure the listener gets depends on which cable is fitted rather than only on level -- the model treats single-ended operation as the second-harmonic-dominant case and balanced operation as the cancelled one. A one-ohm source impedance keeps the electrical damping factor above 30 even on a 32 ohm load, so headphone impedance swings do not print onto the response.',
    provenanceNote:
      'Topology, four-channel architecture and switchable gain are from Bryston published material. Output power into 32, 300 and 600 ohms, THD, signal-to-noise ratio and output impedance are ESTIMATED from the rail voltages and topology of the design; Bryston does not publish a full load-by-load power table for this model.',
  }),

  makeAmp({
    mfr: 'Parasound',
    model: 'Halo JC 1+',
    price: usd(8999, 'APPROXIMATE', 2020, 'Introductory US price per monoblock; Parasound lists the JC 1+ individually rather than as a pair.'),
    year: 2019,
    country: 'United States',
    topology: 'solid-state-ab',
    form: 'monoblock',
    devices: 'John Curl design, 24 bipolar output devices, switchable 10 W or 25 W Class A bias region',
    power: { watts8: 450, watts4: 850, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 29,
    thdPercent: 0.05,
    snrDb: 116,
    bandwidth: [2, 120000],
    dampingFactor: 1200,
    feedbackDb: 30,
    crosstalkDb: -86,
    balancedInput: true,
    nonlinear: { kind: 'class-b-crossover', amount: 0.28, crossoverWidth: 0.0015, headroomDb: 13, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.18, sagTimeMs: 55, recoveryMs: 140, thermalDb: 0.25, thermalTimeMs: 9000, slewRateVus: 110, headroomDb: 2.8 },
    clipping: { kind: 'asymmetric', kneeDb: 2.2, stickiness: 0.08 },
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c8a24a',
    tags: ['john-curl', 'high-bias', 'monoblock', 'high-damping'],
    notes:
      'The switchable high-bias mode holds the output stage in Class A for the first 25 watts, which for normal listening levels into a typical loudspeaker means the crossover region is never traversed at all. That is why the class-B crossover term in this profile is given an unusually narrow width rather than being removed: it exists, but only above a drive level most programme material does not reach. Damping factor above a thousand and a very high slew limit make the amplifier close to load-invariant, so its interaction with a difficult impedance curve is far smaller than the McIntosh entry above.',
    provenanceNote:
      'Rated power into 8 and 4 ohms, signal-to-noise ratio, damping factor and the switchable Class A bias region are Parasound published specifications. The crossover width used by the nonlinear model, the slew limit and the supply sag behaviour are ESTIMATED from the topology and bias arrangement.',
  }),

  makeAmp({
    mfr: 'Rotel',
    model: 'Michi X3',
    price: usd(4999, 'APPROXIMATE', 2021, 'US list at introduction of the first-generation X3.'),
    year: 2020,
    country: 'Japan',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'Class AB output with four bipolar pairs per channel, oversized toroidal transformer, integrated DAC and phono stages',
    power: { watts8: 200, watts4: 350, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 37,
    thdPercent: 0.009,
    snrDb: 103,
    bandwidth: [10, 100000],
    dampingFactor: 350,
    feedbackDb: 32,
    crosstalkDb: -78,
    dynamics: { sagDb: 0.4, sagTimeMs: 65, recoveryMs: 160, thermalDb: 0.12, slewRateVus: 45, headroomDb: 1.6 },
    clipping: { kind: 'asymmetric', kneeDb: 2.4, stickiness: 0.09 },
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6f7278',
    tags: ['integrated', 'toroidal', 'onboard-dac', 'phono'],
    notes:
      'An integrated amplifier is not just a power amplifier in a bigger box, and the model treats it that way: source selection, a volume control and phono and DAC stages all share one chassis and one ground, so channel separation is limited by layout and by the volume control rather than by the output stage, which is why crosstalk here is set roughly 20 dB worse than the separates in this group. The output stage itself is conventional Class AB with moderate bias and substantial feedback, so its residue is crossover-derived and shrinks with level more slowly than its total THD figure suggests.',
    provenanceNote:
      'Rated power into 8 and 4 ohms, THD, signal-to-noise ratio and damping factor are Rotel published specifications for the Michi X3. Channel separation, supply sag and the crossover width used by the nonlinear model are ESTIMATED from the integrated topology.',
  }),

  makeAmp({
    mfr: 'Anthem',
    model: 'STR Preamplifier',
    price: usd(3999, 'APPROXIMATE', 2018, 'US list at introduction.'),
    year: 2017,
    country: 'Canada',
    topology: 'solid-state-a',
    form: 'preamp',
    devices: 'Discrete Class A line stage with relay-switched resistor-ladder attenuator ahead of the gain stage',
    power: { outputVrms: 9.4, confidence: 'ESTIMATED' },
    gainDb: 12,
    thdPercent: 0.0006,
    snrDb: 118,
    bandwidth: [2, 200000],
    outputImpedanceOhms: 100,
    feedbackDb: 26,
    crosstalkDb: -110,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.18, asymmetry: 0.2, bias: 0.35, headroomDb: 20, oversample: 4 },
    dynamics: { sagDb: 0.02, sagTimeMs: 20, recoveryMs: 60, thermalDb: 0.04, slewRateVus: 80, headroomDb: 12 },
    clipping: { kind: 'asymmetric', kneeDb: 1.5, stickiness: 0.05, onsetDbFS: -0.5 },
    confidence: 'ESTIMATED',
    accent: '#8a7fd0',
    tags: ['preamp', 'ladder-attenuator', 'line-stage', 'room-correction-host'],
    notes:
      'The attenuator is a relay-switched resistor ladder placed before the gain stage, which means the noise the listener hears falls as the volume control is turned down instead of sitting at a fixed floor set by a chip attenuator after the gain. That is the single behaviour worth modelling in a line stage: it has enormous voltage headroom, negligible supply sag and a distortion figure low enough that it is effectively a level and impedance transformation. The 100 ohm output impedance still matters into a long cable and a low-impedance power-amplifier input, which is why it is stated rather than assumed to be zero.',
    provenanceNote:
      'The discrete Class A line stage and the relay-switched ladder attenuator are from Anthem published material. Output voltage capability, THD, signal-to-noise ratio, bandwidth and output impedance are ESTIMATED at levels typical of the design class. Anthem Room Correction is a separate DSP path and is deliberately not modelled by this profile.',
  }),
];
